/**
 * Shared utilities for displaying network interface type codes and operational status.
 * Used by both the local dashboard NetworkComponent and the cloud ApplianceStatus component.
 *
 * Type codes correspond to .NET's NetworkInterfaceType enum values.
 */

export interface NetworkInterfaceInfo {
    name: string;
    description?: string;
    networkInterfaceType?: number;
    networkInterfaceTypeName?: string;
    operationalStatus?: string | number;
    isReceiveOnly?: boolean;
    supportsMulticast?: boolean;
    speed?: number;
    macAddress?: string;
    hostName?: string;
    dnsSuffix?: string;
    isDnsEnabled?: boolean;
    gatewayAddresses?: string[];
    dnsAddresses?: string[];
    unicastAddresses?: string[];
    multicastAddresses?: string[];
}

export const INTERFACE_TYPE_NAMES: Record<number, string> = {
    1: 'Other',
    6: 'Ethernet',
    9: 'Token Ring',
    15: 'FDDI',
    23: 'PPP',
    24: 'Loopback',
    28: 'SLIP',
    37: 'ATM',
    48: 'Generic Modem',
    53: 'Fast Ethernet (Tx)',
    62: 'ISDN Basic',
    63: 'ISDN Primary',
    71: 'Wireless 802.11',
    94: 'ADSL',
    114: 'IP over ATM',
    117: 'Gigabit Ethernet',
    131: 'Tunnel',
    143: 'Multirate Symmetric DSL',
    144: 'High Performance Serial Bus',
};

export const INTERFACE_TYPE_DESCRIPTIONS: Record<number, string> = {
    1: 'Unclassified/other network interface type.',
    6: 'Standard IEEE 802.3 Ethernet network adapter.',
    24: 'Loopback interface (local host).',
    71: 'IEEE 802.11 wireless network interface.',
    131: 'Virtual tunnel interface (e.g. VPN).',
};

/** Returns e.g. "6 (Ethernet)" or "99 (Unknown)" */
export function formatInterfaceType(code: number | undefined): string {
    if (code == null) return '—';
    const name = INTERFACE_TYPE_NAMES[code];
    return name ? `${code} (${name})` : `${code} (Unknown)`;
}

/** Returns a human-readable description for use in a tooltip */
export function interfaceTypeTooltip(code: number | undefined): string {
    if (code == null) return 'No type information';
    return INTERFACE_TYPE_DESCRIPTIONS[code] ?? 'No additional description available for this type code.';
}

/** Returns 'success' when Up, 'danger' otherwise — for use with p-tag severity.
 * Handles both string ('Up') and legacy numeric ('1') values. */
export function operationalStatusSeverity(status: string | number | undefined): 'success' | 'danger' {
    if (status === 'Up' || status === 1) return 'success';
    return 'danger';
}

/** Normalise operationalStatus to a display string regardless of whether it arrived as int or string */
export function formatOperationalStatus(status: string | number | undefined): string {
    if (status == null) return 'Unknown';
    if (typeof status === 'number') {
        const names: Record<number, string> = { 1: 'Up', 2: 'Down', 3: 'Testing', 4: 'Unknown', 5: 'Dormant', 6: 'Not Present', 7: 'Lower Layer Down' };
        return names[status] ?? String(status);
    }
    return status;
}

/** Formats bytes-per-second as a readable string e.g. "1 Gbps" */
export function formatNetworkSpeed(bps: number | undefined): string {
    if (bps == null || bps < 0) return '—';
    const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
    let idx = 0;
    let val = bps;
    while (val >= 1000 && idx < units.length - 1) { val /= 1000; idx++; }
    return `${val % 1 === 0 ? val : val.toFixed(1)} ${units[idx]}`;
}

/** Normalizes a MAC address string into AA:BB:CC:DD:EE:FF form, or '—' if invalid */
export function formatMacAddress(raw: string | undefined): string {
    if (!raw) return '—';
    const hex = raw.replace(/[^a-fA-F0-9]/g, '').toUpperCase();
    if (hex.length !== 12) return '—';
    return (hex.match(/.{2}/g) ?? []).join(':');
}
